import { css } from '@emotion/react';

// Color constants
const COLORS = {
    GRAY: '#8D8D8D',
    ORANGE: '#FF903D',
    RED: '#D23E3E',
    PURPLE: '#6200EE',
    BLUE: '#197EF3',
    GREEN: '#59B863',
    BLACK: '#2B2B2B',
};

// Map milestone display names (as returned by calcMilestone) to their background colors
const MILESTONE_COLORS = {
    // Black
    Cancelled: COLORS.BLACK,
    Rejected: COLORS.BLACK,
    // Red
    'On Hold': COLORS.RED,
    Incomplete: COLORS.RED,
    'Quote Declined': COLORS.RED,
    // Gray
    'Order Placed': COLORS.GRAY,
    'Awaiting Quote': COLORS.GRAY,
    'Quote Received': COLORS.GRAY,
    'Quote Awaiting Approval': COLORS.GRAY,
    'Quote Pending': COLORS.GRAY,
    'Quote Approved': COLORS.GRAY,
    'Order Tendered': COLORS.GRAY,
    'Invoiced Awaiting POD': COLORS.GRAY,
    Invoiced: COLORS.GRAY,
    'Invoice Approved': COLORS.GRAY,
    'Order Split': COLORS.GRAY,
    // Orange
    'LTL Pending': COLORS.ORANGE,
    'LTL Scheduled': COLORS.ORANGE,
    'LTL In Transit': COLORS.ORANGE,
    'LTL Arrived': COLORS.ORANGE,
    'On Hand': COLORS.ORANGE,
    'En route to Warehouse': COLORS.ORANGE,
    'Arrived at Warehouse': COLORS.ORANGE,
    'Loading Started': COLORS.ORANGE,
    'Loading Complete': COLORS.ORANGE,
    'On Hand - In Returns': COLORS.ORANGE,
    'Return Trailer Scheduled': COLORS.ORANGE,
    'Return Trailer Picked Up': COLORS.ORANGE,
    'Return Trailer Delivered': COLORS.ORANGE,
    // Purple
    'Pickup Scheduled': COLORS.PURPLE,
    'Pickup Timeframe Entered': COLORS.PURPLE,
    Scheduled: COLORS.PURPLE,
    'Delivery Timeframe Entered': COLORS.PURPLE,
    'Timeframe Entered': COLORS.PURPLE,
    // Blue
    'En route to Pickup': COLORS.BLUE,
    'Arrived at Pickup Location': COLORS.BLUE,
    'Out for Delivery': COLORS.BLUE,
    'Arrived at Delivery Location': COLORS.BLUE,
    'En route to Return Pickup': COLORS.BLUE,
    // Green
    'Pickup Complete': COLORS.GREEN,
    Delivered: COLORS.GREEN,
    Complete: COLORS.GREEN,
    'Return Pickup Complete': COLORS.GREEN,
    'Return Complete': COLORS.GREEN,
    'Invoice Paid': COLORS.GREEN,
};

/**
 * Get the background color for a milestone value
 * @param {string} milestone - The milestone value from calcMilestone
 * @returns {string} - The background color hex code
 */
export const getMilestoneColor = (milestone) => {
    if (!milestone) return COLORS.GRAY;

    // Try exact match first (handles both constant names and display names)
    if (MILESTONE_COLORS[milestone]) {
        return MILESTONE_COLORS[milestone];
    }

    // Try case-insensitive match
    const caseInsensitiveMatch = Object.keys(MILESTONE_COLORS).find(
        (key) => key.toLowerCase() === milestone.toLowerCase()
    );
    if (caseInsensitiveMatch) {
        return MILESTONE_COLORS[caseInsensitiveMatch];
    }

    // Try partial match for variations (e.g., "Arrived (Middle Mile)" contains "Arrived")
    const partialMatch = Object.keys(MILESTONE_COLORS).find((key) => {
        const milestoneLower = milestone.toLowerCase();
        const keyLower = key.toLowerCase();
        return milestoneLower.includes(keyLower) || keyLower.includes(milestoneLower);
    });

    if (partialMatch) {
        return MILESTONE_COLORS[partialMatch];
    }

    // Default to gray if no match found
    return COLORS.GRAY;
};

/**
 * Get CSS styles for a milestone badge
 * @param {string} milestone - The milestone value from calcMilestone
 * @returns {Object} - Emotion CSS object
 */
export const getMilestoneStyles = (milestone) => {
    const backgroundColor = getMilestoneColor(milestone);

    return css`
        display: inline-block;
        vertical-align: middle;
        color: white;
        background-color: ${backgroundColor};
        padding: 5px;
        border-radius: 5px;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        max-width: 100%;
    `;
};
